// Exact decimal helpers over bigint (no floats anywhere in money/qty arithmetic) [MB D8].

/** Rounds num/den to the nearest integer, ties to even (banker's rounding) [03 BR-DM-005]. */
export function roundHalfEven(num: bigint, den: bigint): bigint {
  if (den === 0n) throw new RangeError('division by zero');
  if (den < 0n) return roundHalfEven(-num, -den);
  const negative = num < 0n;
  const n = negative ? -num : num;
  const q = n / den;
  const r = n % den;
  const twice = r * 2n;
  let out = q;
  if (twice > den || (twice === den && q % 2n === 1n)) out = q + 1n;
  return negative ? -out : out;
}

export function pow10(exp: number): bigint {
  if (!Number.isInteger(exp) || exp < 0)
    throw new RangeError('exponent must be a non-negative integer');
  return 10n ** BigInt(exp);
}

const DECIMAL_RE = /^([+-]?)(\d+)(?:\.(\d+))?$/;

/** Parses a plain decimal string into an integer scaled by 10^scale; extra digits are refused. */
export function parseDecimal(text: string, scale: number): bigint {
  const m = DECIMAL_RE.exec(text.trim());
  if (!m) throw new RangeError(`invalid decimal: ${text}`);
  const sign = m[1];
  const int = m[2] ?? '';
  const fraction = m[3] ?? '';
  if (fraction.length > scale && /[1-9]/.test(fraction.slice(scale)))
    throw new RangeError(`too many decimal places (max ${scale}): ${text}`);
  const digits = int + fraction.slice(0, scale).padEnd(scale, '0');
  const v = BigInt(digits);
  return sign === '-' ? -v : v;
}

/** Like parseDecimal but rounds half-even when the input has more than `scale` places. */
export function parseDecimalRounded(text: string, scale: number): bigint {
  const m = DECIMAL_RE.exec(text.trim());
  if (!m) throw new RangeError(`invalid decimal: ${text}`);
  const fracLen = (m[3] ?? '').length;
  if (fracLen <= scale) return parseDecimal(text, scale);
  return roundHalfEven(parseDecimal(text, fracLen), pow10(fracLen - scale));
}

/** Formats an integer scaled by 10^scale as a fixed-point string (machine format, `.` decimal). */
export function formatDecimal(value: bigint, scale: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale);
  return `${negative ? '-' : ''}${int}${scale > 0 ? `.${frac}` : ''}`;
}

/** bigint → number, refusing anything outside the safe-integer range. */
export function toSafeNumber(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER))
    throw new RangeError('value exceeds the safe integer range');
  return Number(value);
}

export function assertSafeInteger(value: number, what: string): void {
  if (!Number.isSafeInteger(value)) throw new RangeError(`${what} must be a safe integer`);
}
